import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, ImagePlus, Loader2, Check, ArrowLeft, Plus, Trash2, RefreshCw, ExternalLink, Crown, Sparkles, AlertTriangle, Play, Pause, Lock, FileText, Upload, DollarSign, Link2, Copy, User, Star, ShieldCheck } from "lucide-react";
import { api, resolveAudio } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import ContentPolicyModal from "@/components/ContentPolicyModal";

const CATEGORIES = ["novel", "fiction", "non-fiction", "sci-fi", "fantasy", "mystery", "thriller", "romance", "novella", "short story", "biography", "self-help", "business", "history", "poetry", "kids"];
const ALL_LANGS = ["english", "spanish", "hindi", "gujarati", "french", "portuguese", "german", "italian", "japanese", "mandarin", "arabic", "russian", "korean", "turkish", "dutch", "polish", "swahili"];
const LANG_NATIVE = {
  english: "English", spanish: "Español", hindi: "हिन्दी", gujarati: "ગુજરાતી",
  french: "Français", portuguese: "Português", german: "Deutsch", italian: "Italiano",
  japanese: "日本語", mandarin: "中文", arabic: "العربية", russian: "Русский",
  korean: "한국어", turkish: "Türkçe", dutch: "Nederlands", polish: "Polski", swahili: "Kiswahili",
};
const ALL_VOICES = [
  { id: "onyx", label: "Onyx (male, baritone)" },
  { id: "echo", label: "Echo (male, warm)" },
  { id: "shimmer", label: "Shimmer (female, warm)" },
  { id: "nova", label: "Nova (female, energetic)" },
];

const PLANS = [
  {
    id: "flip_starter_1mo",
    key: "flip_starter",
    price: "$9.99",
    period: "/ month",
    name: "Flip Starter",
    tagline: "For your first three books",
    accent: "text-violet-300",
    ring: "ring-violet-400/30",
    features: [
      "3 audiobooks · unlimited chapters each",
      "4 AI voices · 5 languages (EN, ES, HI, FR, PT)",
      "Bundled Pulse SEO blog · 4 posts/month",
      "Basic analytics · 100% of tips",
    ],
  },
  {
    id: "flip_pro_1mo",
    key: "flip_pro",
    price: "$19.99",
    period: "/ month",
    name: "Flip Pro",
    tagline: "For serial authors going pro",
    accent: "text-emerald-300",
    ring: "ring-emerald-400/40",
    highlight: true,
    features: [
      "Unlimited books & chapters",
      "All 16 languages · every voice",
      "Priority narration queue + featured placement",
      "Custom author page · advanced analytics",
      "Bundled Pulse · 20 posts/month, 3 sites",
    ],
  },
];

function authHeaders() {
  const t = localStorage.getItem("fliboe_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
}

export default function Publish() {
  const { user, loading: authLoading } = useAuth();
  const [status, setStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [checkoutBusy, setCheckoutBusy] = useState(null);
  const [policyOpen, setPolicyOpen] = useState(false);

  const refreshStatus = async () => {
    if (!user) { setLoadingStatus(false); return; }
    try {
      const { data } = await api.get("/author/status", { headers: authHeaders() });
      setStatus(data);
      if (data.has_flip_plan && !data.policy_accepted_at) setPolicyOpen(true);
    } catch (_) { setStatus(null); }
    finally { setLoadingStatus(false); }
  };
  useEffect(() => { refreshStatus(); }, [user]);

  const startCheckout = async (packageId) => {
    setCheckoutBusy(packageId);
    try {
      const { data } = await api.post("/payments/checkout", { package_id: packageId, origin_url: window.location.origin }, { headers: authHeaders() });
      window.location.href = data.url;
    } catch (e) {
      alert(e?.response?.data?.detail || "Checkout failed. Please try again.");
      setCheckoutBusy(null);
    }
  };

  if (authLoading || loadingStatus) {
    return <main className="max-w-2xl mx-auto px-5 py-24 text-slate-500 fl-mono text-xs uppercase tracking-widest" data-testid="publish-loading">// Loading…</main>;
  }

  // State 1 — not logged in
  if (!user) {
    return (
      <main className="max-w-2xl mx-auto px-5 py-16" data-testid="publish-login-gate">
        <Link to="/flip" className="inline-flex items-center gap-2 fl-mono text-[11px] tracking-widest uppercase text-slate-400 hover:text-violet-400 mb-6">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to Fliboe Flip
        </Link>
        <div className="fl-card p-8 text-center">
          <BookOpen className="w-10 h-10 text-violet-400 mx-auto" />
          <h1 className="fl-display text-3xl font-bold text-white mt-4">Sign in to start publishing</h1>
          <p className="text-slate-400 mt-3 max-w-md mx-auto">Create a free Fliboe account first, then pick a Flip plan and upload your first book.</p>
          <div className="mt-6 flex gap-3 justify-center flex-wrap">
            <Link to={`/signup?next=${encodeURIComponent("/publish")}`} className="fl-btn" data-testid="publish-signup-cta">Create free account</Link>
            <Link to={`/login?next=${encodeURIComponent("/publish")}`} className="fl-btn ghost" data-testid="publish-login-cta">Sign in</Link>
          </div>
        </div>
      </main>
    );
  }

  // State 2 — no active flip plan → plan picker
  if (!status?.has_flip_plan) {
    return (
      <main className="max-w-5xl mx-auto px-5 py-12" data-testid="publish-plan-picker">
        <Link to="/flip" className="inline-flex items-center gap-2 fl-mono text-[11px] tracking-widest uppercase text-slate-400 hover:text-violet-400 mb-6">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to Fliboe Flip
        </Link>
        <div className="flex items-center gap-3 mb-3">
          <Crown className="w-6 h-6 text-violet-400" />
          <div className="fl-mono text-[10px] tracking-[0.3em] uppercase text-violet-400">// Pick your author plan</div>
        </div>
        <h1 className="fl-display text-4xl sm:text-5xl font-black text-white tracking-tighter">Publish audiobooks on Fliboe.</h1>
        <p className="text-slate-400 mt-3 leading-relaxed max-w-2xl">
          Pick a plan, sign up, and start uploading. No manual review — your book goes live the moment narration finishes. Each plan bundles a Pulse SEO blog so your books get discovered on Google, Bing, and ChatGPT.
        </p>

        <div className="mt-10 grid md:grid-cols-2 gap-5" data-testid="plan-picker-grid">
          {PLANS.map(p => (
            <div key={p.id} className={`fl-card p-6 relative ${p.highlight ? "ring-2 " + p.ring : ""}`} data-testid={`plan-${p.key}`}>
              {p.highlight && <div className="absolute -top-3 right-4 bg-emerald-500 text-black text-[10px] fl-mono uppercase tracking-widest px-2 py-0.5 rounded-full font-bold">Most popular</div>}
              <div className={`fl-mono text-[10px] uppercase tracking-[0.25em] ${p.accent}`}>{p.tagline}</div>
              <h2 className="fl-display text-3xl font-black text-white mt-2">{p.name}</h2>
              <div className="mt-3 flex items-baseline gap-1">
                <span className="fl-display text-5xl font-black text-white">{p.price}</span>
                <span className="text-slate-400 text-sm">{p.period}</span>
              </div>
              <ul className="mt-5 space-y-2 text-sm text-slate-300">
                {p.features.map(f => (
                  <li key={f} className="flex items-start gap-2">
                    <Check className={`w-4 h-4 mt-0.5 shrink-0 ${p.accent}`} /> {f}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => startCheckout(p.id)}
                disabled={checkoutBusy === p.id}
                className={`fl-btn w-full mt-6 justify-center ${p.highlight ? "" : "ghost"}`}
                data-testid={`plan-${p.key}-checkout`}
              >
                {checkoutBusy === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <BookOpen className="w-4 h-4" />}
                {checkoutBusy === p.id ? "Redirecting…" : `Start ${p.name}`}
              </button>
            </div>
          ))}
        </div>

        <div className="mt-8 text-center text-slate-500 text-xs">
          Cancel anytime · No commitment · You keep <span className="text-violet-300">100% of tips</span>
        </div>
      </main>
    );
  }

  // State 3 — active Flip plan → author dashboard
  return (
    <>
      <ContentPolicyModal
        open={policyOpen}
        onAccepted={() => { setPolicyOpen(false); refreshStatus(); }}
      />
      <AuthorDashboard status={status} onRefresh={refreshStatus} />
    </>
  );
}

// ────────────────────────────────────────────────────────────
// Author dashboard — books CRUD + inline chapter editor
// ────────────────────────────────────────────────────────────
function AuthorDashboard({ status, onRefresh }) {
  const limits = status.limits;
  const voices = ALL_VOICES.filter(v => limits.voices.includes(v.id));
  const langs = ALL_LANGS.filter(l => limits.languages.includes(l));

  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [openBookId, setOpenBookId] = useState(null);
  const [err, setErr] = useState("");
  const [uploadingCover, setUploadingCover] = useState(false);
  const coverRef = useRef(null);
  const [book, setBook] = useState({
    title: "", author_name: status.email?.split("@")[0] || "", description: "",
    language: langs[0] || "english", voice: voices[0]?.id || "onyx",
    cover_url: "", category: "novel",
  });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/author/books", { headers: authHeaders() });
      setBooks(data || []);
    } catch (_) { setBooks([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const uploadCover = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { setErr("Cover image too large (max 8 MB)."); return; }
    setUploadingCover(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/images/upload", fd, { headers: { ...authHeaders(), "Content-Type": "multipart/form-data" } });
      setBook(b => ({ ...b, cover_url: data.url }));
    } catch (e) { setErr(e?.response?.data?.detail || "Cover upload failed"); }
    finally { setUploadingCover(false); }
  };

  const createBook = async () => {
    setErr("");
    if (!book.title.trim() || !book.author_name.trim()) { setErr("Title and author name are required."); return; }
    setCreating(true);
    try {
      await api.post("/author/books", book, { headers: authHeaders() });
      setBook({ ...book, title: "", description: "", cover_url: "" });
      load();
      onRefresh?.();
    } catch (e) { setErr(e?.response?.data?.detail || "Create failed"); }
    finally { setCreating(false); }
  };

  const deleteBook = async (bookId, title) => {
    if (!window.confirm(`Delete "${title}" and all chapters?`)) return;
    try { await api.delete(`/author/books/${bookId}`, { headers: authHeaders() }); load(); onRefresh?.(); }
    catch (e) { alert(e?.response?.data?.detail || "Delete failed"); }
  };

  const bookLimitReached = status.book_count >= limits.max_books;

  return (
    <main className="max-w-5xl mx-auto px-5 py-12" data-testid="author-dashboard">
      <Link to="/flip" className="inline-flex items-center gap-2 fl-mono text-[11px] tracking-widest uppercase text-slate-400 hover:text-violet-400 mb-6">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to Fliboe Flip
      </Link>

      {/* Header + plan chip */}
      <div className="flex items-center gap-3 flex-wrap">
        <BookOpen className="w-6 h-6 text-violet-400" />
        <h1 className="fl-display text-4xl sm:text-5xl font-black text-white tracking-tighter">Your author studio</h1>
        {status.is_vip ? (
          <span className="ml-2 text-xs px-3 py-1 rounded-full fl-mono uppercase tracking-widest font-bold bg-gradient-to-r from-emerald-500 to-fuchsia-500 text-black inline-flex items-center gap-1" title="Lifetime VIP · all tiers unlocked" data-testid="dash-plan-chip">
            ★ Lifetime VIP
          </span>
        ) : (
          <span className={`ml-2 text-xs px-3 py-1 rounded-full fl-mono uppercase tracking-widest font-bold ${status.plan === "flip_pro" ? "bg-emerald-500 text-black" : "bg-violet-500/15 text-violet-200 border border-violet-400/30"}`} data-testid="dash-plan-chip">
            {status.plan === "flip_pro" ? "Flip Pro" : status.plan === "flip_starter" ? "Flip Starter" : "Admin"}
          </span>
        )}
      </div>
      <p className="text-slate-400 mt-3">
        {status.book_count} of {limits.max_books >= 999 ? "unlimited" : limits.max_books} books used ·
        {" "}<Link to="/pulse" className="text-emerald-400 hover:underline">Manage Pulse posts</Link>
      </p>

      {/* Create book */}
      <section className="fl-card p-6 mt-8" data-testid="author-create-book">
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <div className="flex items-center gap-3">
            <Plus className="w-5 h-5 text-violet-400" />
            <h2 className="fl-display text-xl font-bold text-white">New book</h2>
          </div>
          <Link to="/flip/write" className="fl-btn !py-1.5 !px-3 text-xs !bg-emerald-500 !text-black" data-testid="publish-ai-cta">
            ✨ Write with AI
          </Link>
        </div>

        {bookLimitReached && (
          <div className="mb-4 p-3 rounded-lg bg-emerald-500/10 border border-emerald-400/30 text-emerald-200 text-sm flex items-center gap-2" data-testid="book-limit-banner">
            <AlertTriangle className="w-4 h-4" />
            You&apos;ve hit your book limit. <Link to="/publish" className="underline">Upgrade to Flip Pro</Link> for unlimited books.
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-3">
          <input className="fl-input" placeholder="Book title" value={book.title} onChange={e => setBook({ ...book, title: e.target.value })} data-testid="author-book-title" />
          <input className="fl-input" placeholder="Author name" value={book.author_name} onChange={e => setBook({ ...book, author_name: e.target.value })} data-testid="author-book-author" />

          {/* Cover — upload only */}
          <div className="md:col-span-2 flex gap-3 items-center">
            {book.cover_url ? (
              <img src={book.cover_url} alt="cover" className="w-20 h-28 object-cover rounded border border-white/10 shrink-0" data-testid="author-book-cover-preview" />
            ) : (
              <div className="w-20 h-28 rounded border border-dashed border-white/15 bg-white/5 flex items-center justify-center shrink-0">
                <ImagePlus className="w-5 h-5 text-white/30" />
              </div>
            )}
            <div className="flex-1">
              <input ref={coverRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={e => uploadCover(e.target.files?.[0])} data-testid="author-book-cover-file" />
              <div className="flex gap-2 flex-wrap">
                <button type="button" onClick={() => coverRef.current?.click()} disabled={uploadingCover} className="fl-btn ghost !py-1.5 !px-3 text-xs" data-testid="author-book-cover-upload">
                  {uploadingCover ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImagePlus className="w-3.5 h-3.5" />}
                  {uploadingCover ? "Uploading…" : (book.cover_url ? "Change cover" : "Upload cover")}
                </button>
                {book.cover_url && <button type="button" onClick={() => setBook({ ...book, cover_url: "" })} className="fl-btn ghost !py-1.5 !px-3 text-xs hover:!border-red-400/40 hover:!text-red-300">Remove</button>}
              </div>
              <div className="text-white/40 text-[11px] mt-1.5">JPG, PNG, WEBP or GIF · up to 8 MB.</div>
            </div>
          </div>

          <textarea className="fl-input md:col-span-2" rows={2} placeholder="Book description / synopsis" value={book.description} onChange={e => setBook({ ...book, description: e.target.value })} data-testid="author-book-desc" />
          <select className="fl-input" value={book.language} onChange={e => setBook({ ...book, language: e.target.value })} data-testid="author-book-language">
            {langs.map(l => <option key={l} value={l}>{LANG_NATIVE[l] || l} {LANG_NATIVE[l] ? `(${l})` : ""}</option>)}
          </select>
          <select className="fl-input" value={book.voice} onChange={e => setBook({ ...book, voice: e.target.value })} data-testid="author-book-voice">
            {voices.map(v => <option key={v.id} value={v.id}>{v.label}</option>)}
          </select>
          <select className="fl-input md:col-span-2" value={book.category} onChange={e => setBook({ ...book, category: e.target.value })} data-testid="author-book-category">
            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <button onClick={createBook} disabled={creating || bookLimitReached} className="fl-btn mt-4" data-testid="author-create-book-btn">
          {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} {creating ? "Creating…" : "Create book"}
        </button>
        {err && <div className="text-red-300 text-sm mt-2" data-testid="author-book-err">{err}</div>}
      </section>

      {/* My books */}
      <section className="mt-10">
        <div className="flex items-center justify-between mb-4">
          <h2 className="fl-display text-2xl font-bold text-white">Your books</h2>
          <button onClick={load} className="fl-btn ghost !py-1.5 !px-3 text-xs" data-testid="author-refresh">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>

        {loading ? (
          <div className="text-slate-500 text-sm fl-mono uppercase tracking-widest">// Loading…</div>
        ) : books.length === 0 ? (
          <div className="fl-card p-8 text-center text-slate-500 text-sm">No books yet. Create your first one above.</div>
        ) : (
          <div className="space-y-3">
            {books.map(b => (
              <BookRow key={b.book_id} book={b} limits={limits} voices={voices} langs={langs} isOpen={openBookId === b.book_id} onToggle={() => setOpenBookId(openBookId === b.book_id ? null : b.book_id)} onDelete={() => deleteBook(b.book_id, b.title)} onReload={load} />
            ))}
          </div>
        )}
      </section>

      {/* Author public profile — surfaces on /author/:slug */}
      <AuthorProfileCard />
    </main>
  );
}


// ────────────────────────────────────────────────────────────
// Author public profile — bio + socials + tip jar
// ────────────────────────────────────────────────────────────
function AuthorProfileCard() {
  const [p, setP] = useState({ display_name: "", bio: "", avatar_url: "", twitter: "", instagram: "", youtube: "", website: "", tip_jar_url: "" });
  const [saved, setSaved] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [err, setErr] = useState("");
  const avatarRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/author/profile", { headers: authHeaders() });
        setP({ ...p, ...data });
        if (data.slug) setSaved(data);
      } catch (_) { /* first visit — form starts blank */ }
    })();
  }, []);

  const uploadAvatar = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { setErr("Avatar too large (max 8 MB)."); return; }
    setUploadingAvatar(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/images/upload", fd, { headers: { ...authHeaders(), "Content-Type": "multipart/form-data" } });
      setP(prev => ({ ...prev, avatar_url: data.url }));
    } catch (e) { setErr(e?.response?.data?.detail || "Avatar upload failed"); }
    finally { setUploadingAvatar(false); }
  };

  const save = async () => {
    setErr("");
    setSaving(true);
    try {
      const { data } = await api.post("/author/profile", p, { headers: authHeaders() });
      setSaved(data);
    } catch (e) { setErr(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <section className="fl-card p-6 mt-10" data-testid="author-profile-card">
      <div className="flex items-center gap-3 mb-4">
        <User className="w-5 h-5 text-violet-400" />
        <h2 className="fl-display text-xl font-bold text-white">Your public author page</h2>
        {saved?.slug && (
          <a href={`/author/${saved.slug}`} target="_blank" rel="noreferrer" className="text-violet-300 text-xs hover:underline inline-flex items-center gap-1" data-testid="author-profile-public">
            <ExternalLink className="w-3 h-3" /> /author/{saved.slug}
          </a>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        <input className="fl-input" placeholder="Display name (as shown on your profile)" value={p.display_name || ""} onChange={e => setP({ ...p, display_name: e.target.value })} data-testid="profile-display-name" />
        <input className="fl-input" placeholder="Website (https://…)" value={p.website || ""} onChange={e => setP({ ...p, website: e.target.value })} data-testid="profile-website" />
        <textarea className="fl-input md:col-span-2" rows={3} placeholder="Short bio — who you are, what you write, what readers should expect." value={p.bio || ""} onChange={e => setP({ ...p, bio: e.target.value })} data-testid="profile-bio" />

        {/* Avatar */}
        <div className="md:col-span-2 flex items-center gap-3">
          {p.avatar_url ? (
            <img src={p.avatar_url} alt="avatar" className="w-16 h-16 rounded-full object-cover border border-white/10" data-testid="profile-avatar-preview" />
          ) : (
            <div className="w-16 h-16 rounded-full bg-white/5 border border-dashed border-white/15 flex items-center justify-center">
              <User className="w-6 h-6 text-white/30" />
            </div>
          )}
          <input ref={avatarRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={e => uploadAvatar(e.target.files?.[0])} data-testid="profile-avatar-file" />
          <button type="button" onClick={() => avatarRef.current?.click()} disabled={uploadingAvatar} className="fl-btn ghost !py-1.5 !px-3 text-xs" data-testid="profile-avatar-upload">
            {uploadingAvatar ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImagePlus className="w-3.5 h-3.5" />} {uploadingAvatar ? "Uploading…" : (p.avatar_url ? "Change avatar" : "Upload avatar")}
          </button>
          {p.avatar_url && <button type="button" onClick={() => setP({ ...p, avatar_url: "" })} className="fl-btn ghost !py-1.5 !px-3 text-xs hover:!border-red-400/40 hover:!text-red-300">Remove</button>}
        </div>

        <input className="fl-input" placeholder="Twitter/X @handle or URL" value={p.twitter || ""} onChange={e => setP({ ...p, twitter: e.target.value })} data-testid="profile-twitter" />
        <input className="fl-input" placeholder="Instagram @handle or URL" value={p.instagram || ""} onChange={e => setP({ ...p, instagram: e.target.value })} data-testid="profile-instagram" />
        <input className="fl-input" placeholder="YouTube channel URL" value={p.youtube || ""} onChange={e => setP({ ...p, youtube: e.target.value })} data-testid="profile-youtube" />
        <input className="fl-input" placeholder="Tip jar URL (PayPal, Cash App, Venmo, Ko-fi…)" value={p.tip_jar_url || ""} onChange={e => setP({ ...p, tip_jar_url: e.target.value })} data-testid="profile-tip-jar" />
      </div>

      {err && <div className="text-red-300 text-sm mt-2" data-testid="profile-err">{err}</div>}
      <button onClick={save} disabled={saving} className="fl-btn mt-4" data-testid="profile-save">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} {saving ? "Saving…" : "Save profile"}
      </button>
      {saved && !saving && (
        <div className="text-emerald-300 text-xs mt-2 fl-mono uppercase tracking-widest">// Saved · public at /author/{saved.slug}</div>
      )}
    </section>
  );
}

// ────────────────────────────────────────────────────────────
// One book — chapters + Sales & PDF panel (tabs)
// ────────────────────────────────────────────────────────────
function BookRow({ book, limits, isOpen, onToggle, onDelete, onReload }) {
  const [tab, setTab] = useState("chapters");
  const [ch, setCh] = useState({ title: "", text: "", is_free: true });
  const [adding, setAdding] = useState(false);
  const [chErr, setChErr] = useState("");
  const [chWarn, setChWarn] = useState("");
  const [nowPlaying, setNowPlaying] = useState(null);
  const audioRef = useRef(null);

  const play = (c) => {
    if (!c.audio_url) return;
    if (nowPlaying === c.chapter_id) { audioRef.current?.pause(); setNowPlaying(null); return; }
    if (audioRef.current) audioRef.current.pause();
    const a = new Audio(resolveAudio(c.audio_url));
    a.play().then(() => { audioRef.current = a; setNowPlaying(c.chapter_id); }).catch(() => {});
    a.onended = () => setNowPlaying(null);
  };

  const addChapter = async () => {
    setChErr(""); setChWarn("");
    if (!ch.title.trim() || !ch.text.trim()) { setChErr("Chapter title and text are required."); return; }
    setAdding(true);
    try {
      // Long chapters may take 30-60s (parallel TTS chunks). Explicit timeout so
      // axios/browser doesn't kill the request mid-narration.
      const { data } = await api.post(`/author/books/${book.book_id}/chapters`, ch, { headers: authHeaders(), timeout: 180000 });
      setCh({ title: "", text: "", is_free: true });
      if (data.flagged) setChWarn("Your chapter was published but flagged for review (profanity or copyright match). It won't appear publicly until an admin approves it.");
      onReload();
    } catch (e) {
      const msg = e?.response?.data?.detail || (e?.code === "ECONNABORTED" ? "Narration is taking longer than expected. Try a shorter chapter or retry." : e?.message) || "Chapter add failed";
      setChErr(msg);
    }
    finally { setAdding(false); }
  };

  const deleteChapter = async (chId, title) => {
    if (!window.confirm(`Delete chapter "${title}"?`)) return;
    try { await api.delete(`/author/books/${book.book_id}/chapters/${chId}`, { headers: authHeaders() }); onReload(); }
    catch (e) { alert(e?.response?.data?.detail || "Delete failed"); }
  };

  const [backfillBusy, setBackfillBusy] = useState(false);
  const [backfillMsg, setBackfillMsg] = useState("");
  const chaptersMissingText = (book.chapters || []).filter(c => !(c.text || "").trim() && c.audio_filename).length;
  const backfillText = async () => {
    if (!window.confirm(`Transcribe ${chaptersMissingText} chapter audio file(s) to enable read-along? Uses your Emergent LLM credits.`)) return;
    setBackfillBusy(true); setBackfillMsg("");
    try {
      const { data } = await api.post(`/author/books/${book.book_id}/backfill-text`, {}, { headers: authHeaders(), timeout: 300000 });
      setBackfillMsg(`Filled ${data.filled}, skipped ${data.skipped}, failed ${data.failed}.`);
      onReload();
    } catch (e) { setBackfillMsg(e?.response?.data?.detail || "Backfill failed"); }
    finally { setBackfillBusy(false); }
  };

  const chapters = (book.chapters || []).slice().sort((a, b) => (a.position || 0) - (b.position || 0));

  return (
    <div className="fl-card p-4" data-testid={`author-book-${book.book_id}`}>
      <div className="flex items-center gap-3 flex-wrap">
        {book.cover_url ? (
          <img src={book.cover_url} alt="" className="w-14 h-20 object-cover rounded border border-white/10" />
        ) : (
          <div className="w-14 h-20 rounded bg-white/5 border border-white/10 flex items-center justify-center">
            <BookOpen className="w-5 h-5 text-white/30" />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="text-white font-bold truncate">{book.title}</div>
          <div className="text-white/50 text-xs">{book.language} · {book.voice} · {chapters.length} chapters</div>
          <a href={`/book/${book.slug}`} target="_blank" rel="noreferrer" className="text-violet-300 text-xs hover:underline inline-flex items-center gap-1 mt-1" data-testid={`author-book-public-${book.book_id}`}>
            <ExternalLink className="w-3 h-3" /> /book/{book.slug}
          </a>
        </div>
        <button onClick={onToggle} className="fl-btn ghost !py-1.5 !px-3 text-xs" data-testid={`author-toggle-${book.book_id}`}>{isOpen ? "Close" : "Chapters"}</button>
        <button onClick={onDelete} className="fl-btn ghost !py-1.5 !px-2.5 hover:!border-red-400/40 hover:!text-red-300" data-testid={`author-delete-${book.book_id}`}>
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>

      {isOpen && (
        <div className="mt-4 border-t border-white/5 pt-4">
          {/* Tab switcher */}
          <div className="flex gap-2 mb-4 flex-wrap" data-testid={`author-book-tabs-${book.book_id}`}>
            <button onClick={() => setTab("chapters")} className={`px-3 py-1.5 rounded-full text-xs font-semibold transition ${tab === "chapters" ? "bg-violet-500 text-white" : "bg-white/5 text-white/60 hover:bg-white/10"}`} data-testid={`tab-chapters-${book.book_id}`}>
              <BookOpen className="w-3.5 h-3.5 inline mr-1" /> Chapters
            </button>
            <button onClick={() => setTab("sales")} className={`px-3 py-1.5 rounded-full text-xs font-semibold transition ${tab === "sales" ? "bg-emerald-500 text-black" : "bg-white/5 text-white/60 hover:bg-white/10"}`} data-testid={`tab-sales-${book.book_id}`}>
              <DollarSign className="w-3.5 h-3.5 inline mr-1" /> Sales & PDF
            </button>
          </div>

          {tab === "chapters" && (
          <div>
          {chaptersMissingText > 0 && (
            <div className="mb-3 fl-card p-3 bg-emerald-500/10 border-emerald-400/30 flex items-center justify-between gap-3 flex-wrap" data-testid={`backfill-panel-${book.book_id}`}>
              <div className="text-sm text-white/85">
                <strong className="text-emerald-300">Read-along missing</strong> for {chaptersMissingText} older chapter{chaptersMissingText === 1 ? "" : "s"}. Transcribe to enable it for readers.
                {backfillMsg && <div className="text-xs text-white/60 mt-1">{backfillMsg}</div>}
              </div>
              <button onClick={backfillText} disabled={backfillBusy} className="fl-btn !py-1.5 !px-3 text-xs !bg-emerald-500 !text-black" data-testid={`backfill-btn-${book.book_id}`}>
                {backfillBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} {backfillBusy ? "Transcribing…" : "Enable read-along"}
              </button>
            </div>
          )}
          {chapters.map(c => (
            <div key={c.chapter_id} className={`flex items-center gap-3 py-2 border-b border-white/5 ${c.status === "flagged" ? "opacity-60" : ""}`} data-testid={`author-ch-${c.chapter_id}`}>
              <div className="text-white/50 text-xs w-6">{c.position}.</div>
              <div className="flex-1 min-w-0">
                <div className="text-white text-sm truncate flex items-center gap-2">
                  {c.title}
                  {c.status === "flagged" && (
                    <span title={(c.flags || []).join(", ")} className="text-[10px] fl-mono uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-red-500/15 border border-red-400/40 text-red-300 inline-flex items-center gap-1" data-testid={`author-ch-flag-${c.chapter_id}`}>
                      <Lock className="w-2.5 h-2.5" /> flagged
                    </span>
                  )}
                </div>
                <div className="text-white/40 text-[11px]">{Math.round(c.duration || 0)}s · {c.is_free ? "free" : "paid"}</div>
              </div>
              <button onClick={() => play(c)} className="fl-btn ghost !py-1.5 !px-2.5 text-xs" data-testid={`author-ch-play-${c.chapter_id}`}>
                {nowPlaying === c.chapter_id ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
              </button>
              <button onClick={() => deleteChapter(c.chapter_id, c.title)} className="fl-btn ghost !py-1 !px-2 hover:!border-red-400/40 hover:!text-red-300" data-testid={`author-ch-delete-${c.chapter_id}`}>
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          ))}

          <div className="mt-4 space-y-2" data-testid={`author-add-chapter-${book.book_id}`}>
            <input className="fl-input w-full" placeholder="Chapter title" value={ch.title} onChange={e => setCh({ ...ch, title: e.target.value })} data-testid={`author-ch-title-${book.book_id}`} />
            <textarea className="fl-input w-full" rows={6} placeholder="Paste your chapter text. It will be narrated in your book's voice + language." value={ch.text} onChange={e => setCh({ ...ch, text: e.target.value.slice(0, 8000) })} data-testid={`author-ch-text-${book.book_id}`} />
            <div className="flex items-center justify-between flex-wrap gap-3">
              <label className="flex items-center gap-2 text-sm text-white/70 cursor-pointer">
                <input type="checkbox" checked={ch.is_free} onChange={e => setCh({ ...ch, is_free: e.target.checked })} data-testid={`author-ch-free-${book.book_id}`} />
                Free chapter (no paywall)
              </label>
              <div className="text-white/40 text-[11px]">{ch.text.length}/8000 chars</div>
            </div>
            {chErr && <div className="text-red-300 text-sm" data-testid={`author-ch-err-${book.book_id}`}>{chErr}</div>}
            {chWarn && <div className="text-emerald-300 text-sm flex items-start gap-2" data-testid={`author-ch-warn-${book.book_id}`}><AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> {chWarn}</div>}
            <button onClick={addChapter} disabled={adding} className="fl-btn" data-testid={`author-add-ch-btn-${book.book_id}`}>
              {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {adding ? "Narrating…" : "Add chapter + narrate"}
            </button>
          </div>
          </div>
          )}

          {tab === "sales" && <BookSalesPanel book={book} onReload={onReload} />}
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// Per-book Sales & PDF editor: prices, sales_link, PDF upload, delivery URL
// ────────────────────────────────────────────────────────────
function BookSalesPanel({ book, onReload }) {
  const [s, setS] = useState({
    pdf_price: book.pdf_price || 0,
    audio_price: book.audio_price || 0,
    bundle_price: book.bundle_price || 0,
    sales_link: book.sales_link || "",
    tip_jar_url: book.tip_jar_url || "",
    for_sale: book.for_sale ?? false,
    free_pdf: book.free_pdf ?? false,
  });
  const [saving, setSaving] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [msg, setMsg] = useState("");
  const [copied, setCopied] = useState(false);
  const pdfRef = useRef(null);

  const save = async () => {
    setMsg(""); setSaving(true);
    try {
      await api.patch(`/author/books/${book.book_id}`, s, { headers: authHeaders() });
      setMsg("Saved.");
      onReload();
    } catch (e) { setMsg(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); setTimeout(() => setMsg(""), 3000); }
  };

  // Cloudflare / ingress in front of the app caps request body at ~100 MB.
  // Anything above this soft target gets auto-recompressed with pdf-lib before upload.
  const PDF_SAFE_TARGET_BYTES = 90 * 1024 * 1024;

  const uploadPdf = async (file) => {
    if (!file) return;
    if (file.size > 500 * 1024 * 1024) { setMsg("PDF too large (max 500 MB)."); return; }
    setUploadingPdf(true); setMsg("");
    try {
      let toUpload = file;
      // Auto-resize step: if the PDF is above the safe ingress target, re-serialize
      // with pdf-lib's object streams to shave duplicate/uncompressed data. Pure JS,
      // no server calls, no LLM credits.
      if (file.size > PDF_SAFE_TARGET_BYTES) {
        try {
          setMsg("Optimising PDF for upload…");
          const { PDFDocument } = await import("pdf-lib");
          const bytes = new Uint8Array(await file.arrayBuffer());
          const src = await PDFDocument.load(bytes, { updateMetadata: false });
          const out = await src.save({ useObjectStreams: true, addDefaultPage: false });
          if (out.byteLength < file.size) {
            toUpload = new File([out], file.name.replace(/\.pdf$/i, "") + ".opt.pdf", { type: "application/pdf" });
            const savedMB = ((file.size - out.byteLength) / (1024 * 1024)).toFixed(1);
            setMsg(`Optimised: saved ${savedMB} MB · uploading…`);
          }
        } catch (_) {
          // Optimisation is best-effort; fall through with original file if pdf-lib chokes.
        }
      }
      if (toUpload.size > PDF_SAFE_TARGET_BYTES) {
        setMsg("PDF is still above the 90 MB upload cap. Try compressing images inside the PDF, splitting it into parts, or exporting at a lower DPI.");
        setUploadingPdf(false);
        return;
      }
      const fd = new FormData();
      fd.append("file", toUpload);
      await api.post(`/author/books/${book.book_id}/pdf`, fd, { headers: { ...authHeaders(), "Content-Type": "multipart/form-data" } });
      setMsg("PDF uploaded.");
      onReload();
    } catch (e) { setMsg(e?.response?.data?.detail || "PDF upload failed"); }
    finally { setUploadingPdf(false); setTimeout(() => setMsg(""), 4000); }
  };

  const deletePdf = async () => {
    if (!window.confirm("Remove the PDF from this book?")) return;
    try { await api.delete(`/author/books/${book.book_id}/pdf`, { headers: authHeaders() }); onReload(); }
    catch (e) { setMsg(e?.response?.data?.detail || "Delete failed"); }
  };

  const deliveryUrl = book.pdf_url ? `${window.location.origin}${book.pdf_url}` : "";

  const copyDelivery = async () => {
    if (!deliveryUrl) return;
    try { await navigator.clipboard.writeText(deliveryUrl); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch (_) { /* clipboard blocked — silent */ }
  };

  return (
    <div className="space-y-4" data-testid={`sales-panel-${book.book_id}`}>
      {/* Sales Link Wizard */}
      <div className="p-3 rounded-lg bg-violet-500/5 border border-violet-400/20 text-xs text-violet-100">
        <div className="fl-mono uppercase tracking-widest text-violet-300 text-[10px] mb-1">// How selling works</div>
        Fliboe never touches your money. You pick a checkout provider, paste your buy-link below, and every dollar lands in your account.
        <ul className="mt-2 space-y-0.5 text-white/70">
          <li>· <span className="text-emerald-300">Stripe Payment Link</span> — free, 5-min setup at <code>stripe.com/payments/payment-links</code></li>
          <li>· <span className="text-emerald-300">Gumroad</span> — best for automatic PDF delivery, ~10% fee</li>
          <li>· <span className="text-emerald-300">PayPal.me / Cash App</span> — manual delivery, zero fees</li>
        </ul>
      </div>

      {/* Prices */}
      <div className="grid md:grid-cols-3 gap-3">
        <label className="text-xs text-white/60">
          <div className="fl-mono uppercase tracking-widest text-white/50 mb-1">PDF price ($)</div>
          <input type="number" min={0} step={0.5} className="fl-input" placeholder="0" value={s.pdf_price} onChange={e => setS({ ...s, pdf_price: Number(e.target.value) || 0 })} data-testid={`sales-pdf-price-${book.book_id}`} />
        </label>
        <label className="text-xs text-white/60">
          <div className="fl-mono uppercase tracking-widest text-white/50 mb-1">Audiobook price ($)</div>
          <input type="number" min={0} step={0.5} className="fl-input" placeholder="0" value={s.audio_price} onChange={e => setS({ ...s, audio_price: Number(e.target.value) || 0 })} data-testid={`sales-audio-price-${book.book_id}`} />
        </label>
        <label className="text-xs text-white/60">
          <div className="fl-mono uppercase tracking-widest text-white/50 mb-1">Bundle price ($)</div>
          <input type="number" min={0} step={0.5} className="fl-input" placeholder="0" value={s.bundle_price} onChange={e => setS({ ...s, bundle_price: Number(e.target.value) || 0 })} data-testid={`sales-bundle-price-${book.book_id}`} />
        </label>
      </div>

      {/* Links */}
      <div>
        <div className="fl-mono uppercase tracking-widest text-white/50 text-[10px] mb-1"><Link2 className="w-3 h-3 inline mr-1" /> Your sales link (Stripe / Gumroad / PayPal / etc.)</div>
        <input className="fl-input w-full" placeholder="https://buy.stripe.com/… or https://gumroad.com/l/…" value={s.sales_link} onChange={e => setS({ ...s, sales_link: e.target.value })} data-testid={`sales-link-${book.book_id}`} />
      </div>
      <div>
        <div className="fl-mono uppercase tracking-widest text-white/50 text-[10px] mb-1">Tip jar URL (optional)</div>
        <input className="fl-input w-full" placeholder="https://paypal.me/… or https://cash.app/…" value={s.tip_jar_url} onChange={e => setS({ ...s, tip_jar_url: e.target.value })} data-testid={`sales-tip-jar-${book.book_id}`} />
      </div>

      {/* For sale toggle */}
      <label className="flex items-center gap-2 text-sm text-white/80 cursor-pointer">
        <input type="checkbox" checked={s.for_sale} onChange={e => setS({ ...s, for_sale: e.target.checked, free_pdf: e.target.checked ? false : s.free_pdf })} data-testid={`sales-for-sale-${book.book_id}`} />
        Show Buy buttons on the public book page
      </label>

      {/* Free download toggle — flips the book into give-away mode */}
      <label className={`flex items-start gap-2 text-sm cursor-pointer p-3 rounded-lg border transition ${s.free_pdf ? "bg-emerald-500/10 border-emerald-400/40 text-white" : "bg-white/[0.02] border-white/10 text-white/80"}`}>
        <input type="checkbox" checked={s.free_pdf} onChange={e => setS({ ...s, free_pdf: e.target.checked, for_sale: e.target.checked ? false : s.for_sale })} className="mt-0.5" data-testid={`sales-free-pdf-${book.book_id}`} />
        <div>
          <div className="font-semibold">Offer PDF as a free download</div>
          <div className="text-xs opacity-80 mt-0.5">Anyone visiting the book page can download the PDF for free. Turns off Buy buttons; flip it back off later to sell again.</div>
        </div>
      </label>

      {/* PDF upload + delivery URL */}
      <div className="pt-3 border-t border-white/5">
        <div className="fl-mono uppercase tracking-widest text-white/50 text-[10px] mb-2"><FileText className="w-3 h-3 inline mr-1" /> PDF file</div>
        <input ref={pdfRef} type="file" accept="application/pdf" className="hidden" onChange={e => uploadPdf(e.target.files?.[0])} data-testid={`sales-pdf-file-${book.book_id}`} />
        {book.pdf_url ? (
          <div className="fl-card p-3 bg-white/5" data-testid={`sales-pdf-info-${book.book_id}`}>
            <div className="flex items-center gap-2 flex-wrap">
              <FileText className="w-4 h-4 text-emerald-400" />
              <span className="text-white text-sm">PDF uploaded ({Math.round((book.pdf_size || 0) / 1024)} KB)</span>
              <a href={resolveAudio(book.pdf_url)} target="_blank" rel="noreferrer" className="text-violet-300 text-xs hover:underline">Preview</a>
              <div className="ml-auto flex gap-2">
                <button onClick={() => pdfRef.current?.click()} disabled={uploadingPdf} className="fl-btn ghost !py-1.5 !px-3 text-xs">
                  {uploadingPdf ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Replace
                </button>
                <button onClick={deletePdf} className="fl-btn ghost !py-1.5 !px-3 text-xs hover:!border-red-400/40 hover:!text-red-300"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
            <div className="mt-3">
              <div className="fl-mono uppercase tracking-widest text-white/50 text-[10px] mb-1">Delivery URL — paste this into your Stripe/Gumroad post-purchase redirect</div>
              <div className="flex gap-2 items-center">
                <code className="flex-1 fl-input !py-1.5 text-xs break-all" data-testid={`sales-delivery-url-${book.book_id}`}>{deliveryUrl}</code>
                <button onClick={copyDelivery} className="fl-btn ghost !py-1.5 !px-3 text-xs" data-testid={`sales-copy-url-${book.book_id}`}>
                  {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <button type="button" onClick={() => pdfRef.current?.click()} disabled={uploadingPdf} className="fl-btn ghost !py-2 !px-3 text-sm" data-testid={`sales-pdf-upload-${book.book_id}`}>
            {uploadingPdf ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} {uploadingPdf ? "Uploading…" : "Upload PDF (max 500 MB)"}
          </button>
        )}
      </div>

      {msg && <div className={`text-sm ${msg === "Saved." || msg === "PDF uploaded." ? "text-emerald-300" : "text-red-300"}`} data-testid={`sales-msg-${book.book_id}`}>{msg}</div>}
      <button onClick={save} disabled={saving} className="fl-btn" data-testid={`sales-save-${book.book_id}`}>
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} {saving ? "Saving…" : "Save sales settings"}
      </button>
    </div>
  );
}
